import React, { useState, useEffect } from 'react';
import { Button, Drawer, Descriptions, Input, Space, Alert, Tag, Select } from 'antd';
import AntTable from '../../components/CustomTable/AntTable';
import { getLocalPendingReversals, getLocalPendingReversalDetail, proposeReversalApprove, proposeReversalReject } from '../../services/reversalService';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;

const PendingReversalsTab = () => {
  const [loading, setLoading] = useState(false);
  const [pendingList, setPendingList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [error, setError] = useState(null);

  // Drawer for Detail View
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Role check
  const [isChecker, setIsChecker] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    const rawRoles = localStorage.getItem('roles');
    let userRoles = [];
    try {
      userRoles = JSON.parse(rawRoles || '[]');
      if (!Array.isArray(userRoles)) {
        userRoles = [userRoles];
      }
    } catch {
      userRoles = rawRoles ? [rawRoles] : [];
    }
    setIsChecker(userRoles.includes('OWNER'));
  }, []);

  const fetchPending = async (page = 1, size = pagination.pageSize) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page - 1,
        size,
      };
      if (statusFilter) {
        params.status = statusFilter;
      }
      const res = await getLocalPendingReversals(params);

      // Support Spring page extraction
      const content = res?.data?.content || res?.data || res?.content || (Array.isArray(res) ? res : []);
      const totalElements = res?.data?.totalElements || res?.totalElements || res?.total || content.length;

      const formatted = content.map((item, index) => ({
        ...item,
        id: item.id || item.reference || `pending_rev_${index}`,
      }));

      setPendingList(formatted);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize: size,
        total: totalElements,
      }));
      applyFilter(formatted, searchQuery);
    } catch (err) {
      setError(err.message || 'Failed to fetch pending actions.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (list, query) => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setFilteredList(list);
      return;
    }
    const filtered = list.filter(item =>
      (item.reference && item.reference.toLowerCase().includes(q)) ||
      (item.actionType && item.actionType.toLowerCase().includes(q)) ||
      (item.makerName && String(item.makerName).toLowerCase().includes(q)) ||
      (item.reversalReference && String(item.reversalReference).toLowerCase().includes(q))
    );
    setFilteredList(filtered);
  };

  useEffect(() => {
    fetchPending(1, pagination.pageSize);
  }, [statusFilter]);

  const handleSearch = (val) => {
    setSearchQuery(val);
    applyFilter(pendingList, val);
  };

  const handleTableChange = ({ pagination: newPagination }) => {
    fetchPending(newPagination.current, newPagination.pageSize);
  };

  const handleInspect = async (record) => {
    setDetailLoading(true);
    setDrawerVisible(true);
    try {
      if (record.reference) {
        const detailRes = await getLocalPendingReversalDetail(record.reference);
        if (detailRes && (detailRes.data || detailRes.success)) {
          setSelectedAction(detailRes.data || detailRes);
        } else {
          setSelectedAction(record);
        }
      } else {
        setSelectedAction(record);
      }
    } catch (err) {
      console.error('Error fetching details, falling back to table record:', err);
      setSelectedAction(record);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = (record) => {
    const targetRef = record.reversalReference || record.reference;
    Swal.fire({
      title: 'Approve Reversal Request',
      text: `Are you sure you want to approve reversal: ${targetRef}?`,
      icon: 'success',
      input: 'textarea',
      inputPlaceholder: 'Enter approval reason here (required)...',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Approve & Execute',
      preConfirm: (inputValue) => {
        if (!inputValue || !inputValue.trim()) {
          Swal.showValidationMessage('Approval reason is required');
        }
        return inputValue;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await proposeReversalApprove(targetRef, result.value);
          if (res && (res.success || res.status === true)) {
            NotificationManager.success(res.message || 'Reversal approved successfully');
            setDrawerVisible(false);
            fetchPending();
          } else {
            NotificationManager.error(res?.message || 'Failed to approve reversal');
          }
        } catch (err) {
          NotificationManager.error(err.message || 'An error occurred during approval');
        }
      }
    });
  };

  const handleReject = (record) => {
    const targetRef = record.reversalReference || record.reference;
    Swal.fire({
      title: 'Reject Reversal Request',
      text: `Are you sure you want to reject reversal: ${targetRef}?`,
      icon: 'warning',
      input: 'textarea',
      inputPlaceholder: 'Enter rejection comment here (required)...',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Veto Reject',
      preConfirm: (inputValue) => {
        if (!inputValue || !inputValue.trim()) {
          Swal.showValidationMessage('Rejection comment is required');
        }
        return inputValue;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await proposeReversalReject(targetRef, result.value);
          if (res && (res.success || res.status === true)) {
            NotificationManager.success(res.message || 'Reversal rejected successfully');
            setDrawerVisible(false);
            fetchPending();
          } else {
            NotificationManager.error(res?.message || 'Failed to reject reversal');
          }
        } catch (err) {
          NotificationManager.error(err.message || 'An error occurred during rejection');
        }
      }
    });
  };

  const getStatusTagColor = (status) => {
    const val = String(status || '').toUpperCase();
    if (val === 'APPROVED' || val === 'EXECUTED' || val === 'SUCCESS') return 'success';
    if (val === 'REJECTED' || val === 'FAILED') return 'error';
    if (val === 'PENDING') return 'warning';
    return 'default';
  };

  const columns = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Reversal Ref',
      dataIndex: 'reversalReference',
      key: 'reversalReference',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Action Type',
      dataIndex: 'actionType',
      key: 'actionType',
      render: (text) => (
        <Tag color={String(text || '').toUpperCase().includes('REJECT') ? 'red' : 'green'}>
          {text || 'APPROVE_REVERSAL'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusTagColor(status)} style={{ fontWeight: 600 }}>
          {status || 'PENDING'}
        </Tag>
      ),
    },
    {
      title: 'Maker',
      dataIndex: 'makerName',
      key: 'makerName',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Maker Reason',
      dataIndex: 'makerReason',
      key: 'makerReason',
      ellipsis: true,
      render: (text) => text || 'N/A',
    },
    {
      title: 'Date Proposed',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const status = String(record.status || 'PENDING').toUpperCase();
        const canAction = status === 'PENDING';
        return (
          <Space size="small">
            <Button type="default" size="small" onClick={() => handleInspect(record)}>
              Inspect
            </Button>

            {canAction && (
              <>
                <Button type="primary" size="small" style={{ backgroundColor: '#28a745', borderColor: '#28a745' }} onClick={() => handleApprove(record)}>
                  Approve
                </Button>
                <Button type="primary" danger size="small" onClick={() => handleReject(record)}>
                  Reject
                </Button>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <div className="reversal-toolbar">
        <div className="reversal-filters">
          <Select
            placeholder="Filter by Status"
            allowClear
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
            style={{ width: 180 }}
          >
            <Option value="PENDING">PENDING</Option>
            <Option value="APPROVED">APPROVED</Option>
            <Option value="REJECTED">REJECTED</Option>
            <Option value="EXPIRED">EXPIRED</Option>
            <Option value="FAILED">FAILED</Option>
          </Select>

          <Search
            placeholder="Search proposed actions by reference or maker email..."
            allowClear
            onSearch={handleSearch}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 350 }}
          />
        </div>
        <Button type="default" onClick={() => fetchPending(pagination.current, pagination.pageSize)} loading={loading}>
          Refresh
        </Button>
      </div>

      <AntTable
        loading={loading}
        users={filteredList}
        pagination={pagination}
        handleGetNext={handleTableChange}
        columns={columns}
      />

      <Drawer
        title={`Proposed Reversal Details: ${selectedAction?.reference || ''}`}
        placement="right"
        width={650}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        className="reversal-detail-drawer"
        extra={
          selectedAction && String(selectedAction.status || 'PENDING').toUpperCase() === 'PENDING' && (
            <Space>
              <Button type="primary" style={{ backgroundColor: '#28a745', borderColor: '#28a745' }} onClick={() => handleApprove(selectedAction)}>
                Approve
              </Button>
              <Button type="primary" danger onClick={() => handleReject(selectedAction)}>
                Reject
              </Button>
            </Space>
          )
        }
      >
        {detailLoading ? (
          <div>Loading details...</div>
        ) : selectedAction ? (
          <div className="reversal-detail-container">
            <div className="reversal-detail-section">
              <h4>Proposal Details</h4>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Reference">{selectedAction.reference}</Descriptions.Item>
                <Descriptions.Item label="Reversal Reference">{selectedAction.reversalReference || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Proposed Action">
                  <Tag color={String(selectedAction.actionType || '').toUpperCase().includes('REJECT') ? 'red' : 'green'}>
                    {selectedAction.actionType || 'APPROVE_REVERSAL'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusTagColor(selectedAction.status)} style={{ fontWeight: 600 }}>
                    {selectedAction.status || 'PENDING'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Details">{selectedAction.details || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Date Proposed">
                  {selectedAction.createdAt ? dayjs(selectedAction.createdAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Expires At">
                  {selectedAction.expiresAt ? dayjs(selectedAction.expiresAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </div>

            <div className="reversal-detail-section">
              <h4>Maker / Checker Audit</h4>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Maker ID">{selectedAction.makerId || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Maker Name">{selectedAction.makerName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Maker Reason">{selectedAction.makerReason || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Checker ID">{selectedAction.checkerId || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Checker Name">{selectedAction.checkerName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Checker Comment">{selectedAction.checkerComment || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Checked At">
                  {selectedAction.checkedAt ? dayjs(selectedAction.checkedAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                </Descriptions.Item>
                {selectedAction.executionResult && (
                  <Descriptions.Item label="Execution Result">{selectedAction.executionResult}</Descriptions.Item>
                )}
                {selectedAction.payload && (
                  <Descriptions.Item label="Payload">
                    <pre style={{ fontSize: '11px', margin: 0 }}>
                      {typeof selectedAction.payload === 'object'
                        ? JSON.stringify(selectedAction.payload, null, 2)
                        : String(selectedAction.payload)}
                    </pre>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          </div>
        ) : (
          <div>No record selected</div>
        )}
      </Drawer>
    </div>
  );
};

export default PendingReversalsTab;
